<template>
  <div class="auth-wrapper login-page auth-v2">
    <b-row class="auth-inner m-0">
      <!-- Left Text-->
      <b-col lg="8" class="d-none d-lg-flex login-bg" />

      <!-- In App Browser Warning -->
      <b-col
        v-if="inApp"
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <b-card-text class="mb-2">
            <img class="full-width mb-4 mx-auto d-block" style="max-width: 220px" height="auto" src="~@/assets/images/logo/logo.svg" alt="logo">

            <h3 class="text-center">Purple Visits On Demand</h3>
          </b-card-text>

          <b-card-text class="mt-2" type="warning" style="color: red">
            We have detected issues with your browser as it lacks the necessary support needed for you to have a call.
          </b-card-text>

          <b-card-text class="mt-2">
            Please open this link in a browser capable of making calls. On Desktop and Android we recommend using Chrome, and on iOS Safari.
          </b-card-text>
        </b-col>
      </b-col>
      <!-- /In App Browser Warning-->

      <!-- Login-->
      <b-col
        v-else
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <b-card-text class="mb-2">
            <img class="full-width mb-4 mx-auto d-block" style="max-width: 220px" height="auto" src="~@/assets/images/logo/logo.svg" alt="logo">

            <h3 class="text-center">Purple Visits On Demand</h3>
          </b-card-text>

          <b-card-text v-if="hasLoginError" class="mt-2" type="warning" style="color: red">
            {{ tooManyAttempts ? 'You have made too many login requests and are temporarily locked out. Please wait a while before retrying.' :
              'Passcode incorrect or expired. Please try again.' }}
          </b-card-text>
          <b-card-text v-if="hasInternalError" class="mt-2" type="warning" style="color: red">
            Something went wrong. Please refresh the page and try again.
          </b-card-text>

          <!-- form -->
          <validation-observer ref="loginValidation">
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent
            >
              <!-- Passcode -->
              <b-form-group>
                <div class="d-flex justify-content-between">
                  <label for="login-password">Passcode</label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  name="Passcode"
                  rules="required"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid':null"
                  >
                    <b-form-input
                      id="login-password"
                      v-model="passcode"
                      :state="errors.length > 0 ? false:null"
                      class="form-control-merge"
                      :type="passwordFieldType"
                      name="login-password"
                      placeholder="············"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <b-form-text>Please enter the passcode on the email/text you received.</b-form-text>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- submit buttons -->
              <b-button
                type="submit"
                variant="primary"
                block
                @click="validationForm"
              >
                Proceed to visit
              </b-button>
            </b-form>
          </validation-observer>
        </b-col>
      </b-col>
      <!-- /Login-->
    </b-row>
  </div>
</template>

<script>
import { togglePasswordVisibility } from '@core/mixins/ui/forms';
import { required } from '@validations'
import useJwt from '@/auth/jwt/useJwt'
import { logger } from '@/util/logger';
import OffsiteMagicLinkService from '@/services/OffsiteMagicLinkService';
import CallService from '@/services/CallService';
import InApp from 'detect-inapp';
import OffsiteUsersService from '../../services/OffsiteUsersService';

export default {
  mixins: [togglePasswordVisibility],
  data() {
    return {
      hasLoginError: false,
      hasInternalError: false,
      tooManyAttempts: false,
      isLoggingIn: false,
      passcode: '',
      required,
      inApp: false,
    };
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  mounted() {
    // const inapp = new InApp(navigator.userAgent || navigator.vendor || window.opera);
    // // Check they are using either chrome, firefox or safari, edge
    // this.inApp = !(inapp.browser.toLowerCase().includes('chrome') || inapp.browser.toLowerCase().includes('firefox') || inapp.browser.toLowerCase().includes('safari') || inapp.browser.toLowerCase().includes('edge'))
    // if (this.inApp) {
    //   logger.error('Magic link opened in in-app browser', null, inapp, 'LoginMagicLink:mounted');
    // }
  },
  methods: {
    validationForm() {
      this.$refs.loginValidation.validate().then(success => {
        if (success) {
          this.$store.dispatch('auth/clearLocalUser');

          setTimeout(() => {
            this.submit();
          });
        }
      })
    },
    async submit() {
      this.hasLoginError = false;
      this.hasInternalError = false;
      const { slug } = this.$route.params;

      // Login & Fetch user
      try {
        await this.login(slug);
      } catch (err) {
        console.error('Error logging in ', err);
        this.hasLoginError = err.response.status < 500;
        this.hasInternalError = err.response.status >= 500;
        this.tooManyAttempts = err.response.status === 429;
      }

      // Fetch magic link
      let magicLink;
      try {
        magicLink = await this.fetchMagicLink(slug);
        // Set offsite expiry in local storage
        localStorage.setItem(useJwt.jwtConfig.storageExpiryName, magicLink.expiry);
        localStorage.setItem(useJwt.jwtConfig.storageIntakeVisitId, magicLink.visit_id);
      } catch (err) {
        logger.error('Error getting magic link info', err, { slug }, 'LoginMagicLink:submit');
        this.hasInternalError = true;
        // Clear user login
        this.clearUserInfo();
      }

      // Fetch visit & check correct status
      try {
        await this.fetchVisit(magicLink.visit_id);
        // Redirect to the waiting room
        this.$router.push({ name: 'waiting-room', params: { id: magicLink.visit_id } });
      } catch (err) {
        console.error('Error fetching visit ', err);
        this.hasLoginError = true;
        if (err.toString().includes('Cannot proceed')) {
          this.$toast.error(err.toString(), {
            toastClassName: ['toast-std', 'warning-toast'], timeout: 5000,
          });
        } else {
          // eslint-disable-next-line no-underscore-dangle
          logger.error('Error getting visit', err, { id: magicLink.visit_id, magicLinkId: magicLink._id.toString() }, 'LoginMagicLink:submit')
        }
        // Clear user login
        this.clearUserInfo();
      }
    },
    async login(slug) {
      // Login
      const res = await useJwt.loginMagicLink(slug, this.passcode);
      useJwt.setToken(res.data.token);
      // Fetch user info
      const userRes = await OffsiteUsersService.getUser();
      this.$store.dispatch('auth/setLocalUser', userRes.data);
    },
    async fetchMagicLink(slug) {
      return OffsiteMagicLinkService.get(slug);
    },
    async fetchVisit(visitId) {
      const visitRes = await CallService.getCall(visitId);
      const visit = visitRes.data;
      if (visit.status === 'Approved') {
        return visit;
      } if (visit.status === 'Completed') {
        throw new Error('Cannot proceed as visit is marked as completed');
      } else if (visit.status === 'Cancelled') {
        throw new Error('Cannot proceed as the visit has been cancelled');
      } else {
        throw new Error('Cannot proceed as the visit is not in the correct state');
      }
    },
    clearUserInfo() {
      this.$store.dispatch('auth/clearLocalUser');
      this.$store.commit('auth/SET_USER', null);
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)
      localStorage.removeItem(useJwt.jwtConfig.storageExpiryName)
      localStorage.removeItem(useJwt.jwtConfig.storageIntakeVisitId)
      localStorage.removeItem('userData')
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';

.login-page {
  .login-bg {
    background: linear-gradient(to bottom, #9938B9 0%,black 99%);
    background-size: cover;
    background-position: center;
    //height: 100vh;
  }
}
</style>
